import React from 'react';
import VehicleAutoCompleteComponent from '@/main/components/shared/vehicle/VehicleAutoCompleteComponent';
import { VehicleAutoCompleteResponse } from '@/main/models/response';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogAutoCompleteVehicleControlled from '@/main/components/shared/inputAutoComplete/autoCompleteVehicleControlled';
import { getTranslateByKey } from '@/main/services/utils';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import { Control, FieldErrors, UseFormSetValue } from 'react-hook-form';
import { VehicleAutoCompleteTypeEnum } from '@/main/services/enum/VehicleAutoCompleteTypeEnum';
import { KeyValue } from '@/main/models/common';
import { YearState } from '@/main/store/vehicle/year';
import { ManufacturerState } from '@/main/store/vehicle/manufacturer';
import { ModelState } from '@/main/store/vehicle/model';
import { SubModelState } from '@/main/store/vehicle/subModel';
import ContainerBox from '../../../components/containerBox/containerBox';

import * as S from '../styles';
import { ICreateOrderForm } from '../stepOneScreen';

interface IVehicleInformation {
  control: Control<ICreateOrderForm, any>;
  errors: FieldErrors<ICreateOrderForm>;
  setValue: UseFormSetValue<ICreateOrderForm>;
  loadOptionsByVehicle: (vehicle: VehicleAutoCompleteResponse) => void;
  yearReducer: YearState;
  isDisabledYearDrop: () => boolean;
  manufacturerReducer: ManufacturerState;
  isDisabledManufacturerDrop: () => boolean;
  callManufacturerApiDomain: (optionSelected: KeyValue | null) => void;
  modelReducer: ModelState;
  isDisabledModelDrop: () => boolean;
  callModelApiDomain: (optionSelected: KeyValue | null) => void;
  subModelReducer: SubModelState;
  isDisabledSubModelDrop: () => boolean;
  callSubModelApiDomain: (optionSelected: KeyValue | null) => void;
  hasItemsOfCarts: boolean;
  integration: boolean;
  isEditQuote: boolean;
  isQuoteCanceled?: boolean;
}
function VehicleInformation({
  control,
  errors,
  setValue,
  loadOptionsByVehicle,
  yearReducer,
  isDisabledYearDrop,
  manufacturerReducer,
  isDisabledManufacturerDrop,
  callManufacturerApiDomain,
  modelReducer,
  isDisabledModelDrop,
  callModelApiDomain,
  subModelReducer,
  isDisabledSubModelDrop,
  callSubModelApiDomain,
  hasItemsOfCarts,
  integration,
  isEditQuote,
  isQuoteCanceled = false
}: IVehicleInformation) {
  return (
    <ContainerBox title={getTranslateByKey('information.vehicleInformation')}>
      <S.ContainerAutoCompleteVehicle>
        <VehicleAutoCompleteComponent
          handleChangeVehicle={(vehicle: VehicleAutoCompleteResponse) => {
            loadOptionsByVehicle(vehicle);
          }}
          vehicleAutoCompleteType={VehicleAutoCompleteTypeEnum.Full}
          hasBorderStyle
          label={getTranslateByKey('information.vehicle')}
          disabled={hasItemsOfCarts || isQuoteCanceled || isEditQuote}
        />
      </S.ContainerAutoCompleteVehicle>
      <S.RowInputsVehicleInformation>
        <KogAutoCompleteControlled
          id="id-createOrder-autocomplete-year"
          nameCustom="year"
          label={getTranslateByKey('information.year')}
          control={control}
          rulesCustom={{ required: true }}
          hasErrorCustom={!!errors.year}
          errorMessageCustom={
            errors.year?.type === 'required'
              ? getTranslateByKey('information.yearRequired', true)
              : ''
          }
          options={yearReducer.years.data}
          isLoading={yearReducer.years.isLoading}
          onChangeCustom={(optionSelected: KeyValue | null) => {
            callManufacturerApiDomain(optionSelected);
          }}
          disabled={isDisabledYearDrop()}
          setValueCustom={setValue}
        />
        <KogAutoCompleteVehicleControlled
          id="id-createOrder-autocomplete-manufacturer"
          nameCustom="manufacturer"
          label={getTranslateByKey('information.manufacturer')}
          control={control}
          options={manufacturerReducer.manufacturers.data}
          isLoading={manufacturerReducer.manufacturers.isLoading}
          rulesCustom={{ required: true }}
          hasErrorCustom={!!errors.manufacturer}
          errorMessageCustom={
            errors.manufacturer?.type === 'required'
              ? getTranslateByKey('information.manufacturerRequired', true)
              : ''
          }
          onChangeCustom={(optionSelected: KeyValue | null) => {
            callModelApiDomain(optionSelected);
          }}
          setValueCustom={setValue}
          disabled={isDisabledManufacturerDrop()}
        />
        <KogAutoCompleteVehicleControlled
          id="id-createOrder-autocomplete-model"
          nameCustom="model"
          label={getTranslateByKey('information.model')}
          control={control}
          options={modelReducer.models.data}
          isLoading={modelReducer.models.isLoading}
          rulesCustom={{ required: true }}
          hasErrorCustom={!!errors.model}
          errorMessageCustom={
            errors.model?.type === 'required'
              ? getTranslateByKey('information.modelRequired', true)
              : ''
          }
          onChangeCustom={(optionSelected: KeyValue | null) => {
            callSubModelApiDomain(optionSelected);
          }}
          setValueCustom={setValue}
          disabled={isDisabledModelDrop()}
        />
        <KogAutoCompleteVehicleControlled
          id="id-createOrder-autocomplete-trim"
          nameCustom="trim"
          label={getTranslateByKey('information.trim')}
          control={control}
          options={subModelReducer.subModels.data}
          isLoading={subModelReducer.subModels.isLoading}
          rulesCustom={{ required: true }}
          hasErrorCustom={!!errors.trim}
          errorMessageCustom={
            errors.trim?.type === 'required'
              ? getTranslateByKey('information.trimRequired', true)
              : ''
          }
          onChangeCustom={() => {}}
          setValueCustom={setValue}
          disabled={isDisabledSubModelDrop()}
        />
      </S.RowInputsVehicleInformation>
      <S.RowInputsVehicleInformation>
        <KogInputTextControlledNew
          id="id-createOrder-inputText-licensePlate"
          label={getTranslateByKey('information.licensePlate')}
          name="licensePlate"
          control={control}
          controllerProps={{ rules: { required: false, maxLength: 200 } }}
          disabled={integration || isQuoteCanceled}
        />
        <KogInputTextControlledNew
          id="id-createOrder-inputText-vin"
          label={getTranslateByKey('information.vin')}
          name="vin"
          control={control}
          disabled={integration || isQuoteCanceled}
        />
        <KogInputTextControlledNew
          id="id-createOrder-inputText-vehicleColor"
          label={getTranslateByKey('information.vehicleColor')}
          name="vehicleColor"
          control={control}
          disabled={integration || isQuoteCanceled}
        />
        <KogInputTextControlledNew
          id="id-createOrder-inputText-numbersOfDoor"
          label={getTranslateByKey('information.numbersOfDoor')}
          hasOnlyNumber
          name="numbersOfDoor"
          control={control}
          disabled={integration || isQuoteCanceled}
        />
      </S.RowInputsVehicleInformation>
    </ContainerBox>
  );
}

export default VehicleInformation;
